import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { LineItemFooter } from "components/formFields/line/LineItemFooter";
import { DragHandleHeading } from "components/utility/DragHandle";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { i18n } from "services/i18nService";
import { expandItemGroup } from "services/sosInventoryService/expandItemGroup";

import { useBins } from "hooks/useBins";
import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";

import { MemoizedLineItem } from "views/RentalReturns/RentalReturn/MemoizedLineItem";

import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    record,
    relatedRecords,
    objectType,
    customer,
    lines,
    items,
    totals,
    itemsCount,
    addItem,
    classes,
    classesCount,
    lineHandler,
    setClearLines,
    lineItemsErrors,
  } = props;

  const jobWorkcenters = useJobWorkcenters();

  const toBins = useBins(record?.toLocation);

  const {
    readOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  } = useLineItemHandlers(
    record?.id,
    lines,
    lineHandler,
    EMPTY_LINE_ITEM[objectType]
  );

  // SETTINGS
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );
  const showWeightOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnSales
  );
  const showVolumeOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnSales
  );

  if (!lines) {
    return null;
  }

  return (
    <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <DragHandleHeading />
            <EditLineButtonHeading recordId={record.id} />
            <ClearLines setClearLines={setClearLines} />
            <LineTableHeadingCell>{i18n("columns.Line")}</LineTableHeadingCell>
            <LineInventoryItemHeading lineHandler={lineHandler} />
            <LineColumnHeading labelCode="Description" />
            <LineColumnHeading labelCode="Quantity" />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            {showWeightOnSales && <LineColumnHeading labelCode="Weight" />}
            {showVolumeOnSales && <LineColumnHeading labelCode="Volume" />}
            <LineColumnHeading labelCode="AppliedTo" />
            {classEnabled && (
              <LineColumnFillHeading
                labelCode="Class"
                field="class"
                lineHandler={lineHandler}
              />
            )}
            {jobEnabled && (
              <LineColumnFillHeading
                labelCode="Job"
                field="jobWorkcenter"
                lineHandler={lineHandler}
              />
            )}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={handleDrop}>
          <Droppable droppableId="lineItems">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {lines.map((line, index) => {
                  const lineReadOnly = readOnlyLineIds.has(index);

                  return (
                    <MemoizedLineItem
                      key={line.id || `temp-${index}`}
                      line={line}
                      index={index}
                      record={record}
                      relatedRecords={relatedRecords}
                      customer={customer}
                      lineHandler={lineHandler}
                      expandItemGroup={(line) =>
                        expandItemGroup(
                          line,
                          lineHandler,
                          objectType,
                          record,
                          null,
                          relatedRecords.toLocation
                        )
                      }
                      items={items}
                      itemsCount={itemsCount}
                      addItem={addItem}
                      classes={classes}
                      classesCount={classesCount}
                      jobWorkcenters={jobWorkcenters}
                      lineItemsErrors={lineItemsErrors}
                      toBins={toBins}
                      insertEmptyLine={insertEmptyLine}
                      objectType={objectType}
                      isLastLine={index === lines.length - 1}
                      setLineEdit={setLineEdit}
                      lineReadOnly={lineReadOnly}
                      nextLineReadOnly={readOnlyLineIds.has(index + 1)}
                      handleDeleteLine={handleDeleteLine}
                    />
                  );
                })}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        <LineItemFooter
          showWeight={showWeightOnSales}
          showVolume={showVolumeOnSales}
          totals={totals}
          firstCellSpan={4}
          lineHandler={lineHandler}
          objectType={objectType}
          recordId={record.id}
        />
      </Table>
    </TableContainer>
  );
}
